import { useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@mui/material';
import { UserDeletionMode } from '../../types/user';

interface UserDeleteDialogProps {
    open: boolean;
    loading?: boolean;
    userId: number | null;
    onClose: () => void;
    onConfirm: (mode: UserDeletionMode) => void;
}

export default function UserDeleteDialog({
    open,
    loading = false,
    userId,
    onClose,
    onConfirm,
}: UserDeleteDialogProps) {
    const [mode, setMode] = useState<UserDeletionMode>(UserDeletionMode.SOFT_DELETE);
    const [confirmInput, setConfirmInput] = useState('');

    const handleClose = () => {
        setMode(UserDeletionMode.SOFT_DELETE);
        setConfirmInput('');
        onClose();
    };

    const handleConfirm = () => {
        onConfirm(mode);
        setMode(UserDeletionMode.SOFT_DELETE);
        setConfirmInput('');
    };

    const isHardDelete = mode === UserDeletionMode.HARD_DELETE;
    const isConfirmDisabled = isHardDelete && confirmInput !== String(userId);

    return (
        <Dialog open={open} onClose={loading ? undefined : handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>사용자 탈퇴 처리</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    탈퇴 처리 방식을 선택하세요.
                </Typography>

                <FormControl fullWidth>
                    <RadioGroup
                        value={mode}
                        onChange={(e) => {
                            setMode(e.target.value as UserDeletionMode);
                            setConfirmInput('');
                        }}
                    >
                        <FormControlLabel
                            value={UserDeletionMode.SOFT_DELETE}
                            control={<Radio />}
                            label="데이터 보관 (소프트 삭제)"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mb: 1.5 }}>
                            사용자 계정만 비활성화하고 학습 데이터는 유지합니다.
                        </Typography>

                        <FormControlLabel
                            value={UserDeletionMode.HARD_DELETE}
                            control={<Radio color="error" />}
                            label="데이터 삭제 (완전 삭제)"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                            사용자 테이블을 포함한 사용자 데이터를 삭제합니다. 이 작업은 되돌릴 수 없습니다.
                        </Typography>
                    </RadioGroup>
                </FormControl>

                {isHardDelete && (
                    <>
                        <Alert severity="error" sx={{ mt: 2 }}>
                            사용자의 학습 기록, 구매 및 구독 이력 등 연결된 데이터가 함께 삭제됩니다.
                        </Alert>
                        <TextField
                            fullWidth
                            size="small"
                            sx={{ mt: 2 }}
                            label={`확인을 위해 사용자 ID "${userId}"를 입력하세요`}
                            value={confirmInput}
                            onChange={(e) => setConfirmInput(e.target.value)}
                            error={confirmInput.length > 0 && confirmInput !== String(userId)}
                            helperText={
                                confirmInput.length > 0 && confirmInput !== String(userId)
                                    ? '사용자 ID가 일치하지 않습니다.'
                                    : undefined
                            }
                        />
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    취소
                </Button>
                <Button
                    variant="contained"
                    color={isHardDelete ? 'error' : 'primary'}
                    onClick={handleConfirm}
                    disabled={loading || isConfirmDisabled}
                >
                    탈퇴 처리
                </Button>
            </DialogActions>
        </Dialog>
    );
}
